import React, { useContext } from "react";
import { FormattedMessage, FormattedHTMLMessage } from "react-intl-phraseapp";
import { Button, Card } from "@cmsgov/ds-medicare-gov";
import { useTranslate, $$Whole, $$, useFormatMessage } from "../../../helpers";
import { CheckmarkIcon } from "../../../components";
import {
  planBenefitMapping,
  MedigapPlan,
  MedigapPlanPremiumRange,
  MedigapPlanType,
} from "../utils/plans";
import PhasedOutDisclaimer from "./PhasedOutDisclaimer";
import { MedigapBenefitsHelpDrawer } from "../HelpDrawers/MedigapBenefitsHelpDrawer";
import planDataUtils from "../../../helpers/planDataUtils";
import { useFlags } from "launchdarkly-react-client-sdk";
import routes from "../../../app/routes";
import { Link } from "react-router-dom";
import { AppContext } from "../../../app/store";
import { Gender } from "../../../@types";
import { v1 as uuidv1 } from "uuid";
import { TooltipWithClose } from "../../../components";
import {
  AnalyticsActionType,
  AnalyticsButtonStyle,
  AnalyticsButtonType,
} from "../../../app/contexts/Analytics/types";

interface MedigapPlanCardProps {
  plan: MedigapPlan;
  premiumRange: MedigapPlanPremiumRange;
}

const MedigapPlanCard: React.FunctionComponent<MedigapPlanCardProps> = ({
  plan,
  premiumRange,
}) => {
  const t = useTranslate();
  const { state, dispatch } = useContext(AppContext);
  const { demographicInfo } = state;
  const { mctCurrentPlanYear } = useFlags();
  const FM = useFormatMessage();
  const planBenefits = plan.plan_benefits.split(",");
  const partBPremium = planDataUtils.getPartBPremiumForYear(
    mctCurrentPlanYear || new Date().getFullYear().toString()
  );
  const hasCopayTooltip = [MedigapPlanType.N, MedigapPlanType.MN_N].includes(
    plan.plan_type as MedigapPlanType
  );
  let monthlyCostMessage = [t("medigap.premiums_range.demo_data.none")];

  if (demographicInfo) {
    const { age, gender, tobacco } = demographicInfo;
    monthlyCostMessage = [t("medigap.premiums_range.demo_data.for_a")];
    if (age) {
      monthlyCostMessage.push(
        Number(age) > 64
          ? FM(
              { id: "medigap.premiums_range.demo_data.year_old" },
              { age: age }
            )
          : t("medigap.premiums_range.demo_data.younger_than_65")
      );
    }
    if (gender === Gender.FEMALE) {
      monthlyCostMessage.push(t("enroll_form.female"));
    } else if (gender === Gender.MALE) {
      monthlyCostMessage.push(t("enroll_form.male"));
    }
    if (tobacco !== undefined) {
      if (!age && !gender) {
        monthlyCostMessage.push(t("medigap.premiums_range.demo_data.person"));
      }
      monthlyCostMessage.push(
        tobacco
          ? t("medigap.premiums_range.demo_data.tobacco_yes")
          : t("medigap.premiums_range.demo_data.tobacco_no")
      );
    }
  }

  const focusOnDemographicComponent = (): void => {
    dispatch({
      type: AnalyticsActionType.SEND_TEALIUM_EVENT,
      settings: {
        event_action: "Step 1: Decide what plans you want",
        event_label: `Result: Get a more accurate price Medigap Plan ${plan.plan_type}`,
      },
    });
    const focusElement = document.querySelector(
      ".AccuratePriceContainer"
    ) as HTMLInputElement;
    focusElement.focus();
  };

  /*
    aria-describedby: Used to present plan heading text after the target acnhor link text is presented.
    aria-labelledby: Can be used to concatenate and adjust ordering of the presented text value.

    // Read heading followed by link text
    <h1 id={headingId}/>
    <a aria-labelledby="headingId  linkId" />
  */
  const headingId = uuidv1();
  const planDetailsLinkId = uuidv1();
  const viewPoliciesId = uuidv1();

  return (
    <Card className="MedigapPlanCard e2e-medigap-plan-card">
      <section className="MedigapPlanCard__header">
        <h2 id={headingId}>{t(plan.plan_name)}</h2>
        <PhasedOutDisclaimer plan={plan} className="MedigapPlanCard__info" />
      </section>
      <section className="MedigapPlanCard__actions">
        <div className="mct-hide-print">
          <Link
            id={planDetailsLinkId}
            aria-labelledby={`${headingId} ${planDetailsLinkId}`}
            to={routes.medigap.planDetails.replace(
              ":medigapPlanType",
              plan.plan_type
            )}
            className="ds-c-button ds-c-button--solid ds-c-button--alternate"
            onClick={(): void => {
              dispatch({
                type: AnalyticsActionType.SEND_TEALIUM_EVENT,
                settings: {
                  event_action: "Step 1: Decide what plans you want",
                  event_label: `CTA: Plan Details Medigap Plan ${plan.plan_type}`,
                },
              });
            }}
          >
            {t("medigap.card.button.plan_details")}
          </Link>
          <Link
            id={viewPoliciesId}
            aria-labelledby={`${headingId} ${viewPoliciesId}`}
            to={routes.medigap.policies.replace(
              ":medigapPlanType",
              plan.plan_type
            )}
            className="ds-c-button ds-c-button--solid e2e-view-policies"
            onClick={(): void => {
              dispatch({
                type: AnalyticsActionType.SEND_TEALIUM_EVENT,
                settings: {
                  event_action: "Step 1: Decide what plans you want",
                  event_label: `CTA: View Policies Medigap Plan ${plan.plan_type}`,
                },
              });
            }}
          >
            {t("medigap.card.button.view_policies")}
          </Link>
        </div>
      </section>
      <div className="MedigapPlanCard__body">
        <section className="MedigapPlanCard__section_column--left">
          <h3 className="MedigapPlanCard__section_heading">
            {t("medigap.card.section_heading.monthly_cost")}
          </h3>
          <>
            <FormattedMessage
              id="medigap.premiums_range"
              values={{
                range: (
                  <b>
                    {$$Whole(Math.round(premiumRange.monthly_rate_min))}-
                    {$$Whole(Math.round(premiumRange.monthly_rate_max))}
                  </b>
                ),
              }}
            />{" "}
            {monthlyCostMessage.join(" ")}
          </>
          <FormattedHTMLMessage id="medigap.household_discounts" tagName="p" />
          <p>
            {t("plan_card.does_not_include")}:
            <br />
            {$$(partBPremium)} {t("plan_terms.partb_premium")}
          </p>
          <div className="ds-u-margin-top--2 mct-hide-print">
            <Button
              variation="ghost"
              className="ds-u-padding--0 ds-u-text-align--left"
              onClick={() => {
                dispatch({
                  type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
                  settings: {
                    button: {
                      buttonStyle: AnalyticsButtonStyle.TRANSPARENT,
                      buttonType: AnalyticsButtonType.BUTTON,
                      text: t("medigap.get_more_accurate_price"),
                    },
                  },
                });
                focusOnDemographicComponent();
              }}
            >
              {t("medigap.get_more_accurate_price")}
            </Button>
          </div>
        </section>
        <div className="MedigapPlanCard__divider"></div>
        <div className="MedigapPlanCard__section_column--middle">
          <section>
            <h3 className="MedigapPlanCard__section_heading">
              {t("medigap.card.section_heading.copays")}
            </h3>
            <div className="MedigapPlanCard__line_item">
              <b>{t(plan.part_b_coinsurance)}</b>{" "}
              {plan.part_b_coinsurance_note && t(plan.part_b_coinsurance_note)}{" "}
              {hasCopayTooltip && (
                <TooltipWithClose
                  title={
                    <p className="ds-u-font-size--sm">
                      {t("medigap.20_50_copays.tooltip")}
                    </p>
                  }
                >
                  {t("medigap.20_50_copays")}
                </TooltipWithClose>
              )}
            </div>
          </section>
          <section>
            <h3 className="MedigapPlanCard__section_heading">
              {t("medigap.card.section_heading.deductibles")}
            </h3>
            {plan.total_deductible && (
              <div className="MedigapPlanCard__line_item">
                <b>{plan.total_deductible}</b>{" "}
                {t("medigap.total_deductible_info")}
              </div>
            )}
            <div>
              <div className="MedigapPlanCard__line_item">
                <b>{plan.part_a_deductible}</b>{" "}
                {plan.part_a_deductible_note && t(plan.part_a_deductible_note)}{" "}
                <TooltipWithClose
                  title={
                    <p className="ds-u-font-size--sm">
                      {t("medigap.part_a.deductible.tooltip")}
                    </p>
                  }
                >
                  {t("medigap.part_a.deductible")}
                </TooltipWithClose>
              </div>
              <div className="MedigapPlanCard__line_item">
                <b>{plan.part_b_deductible}</b>{" "}
                {plan.part_b_deductible_note && t(plan.part_b_deductible_note)}{" "}
                <TooltipWithClose
                  title={
                    <p className="ds-u-font-size--sm">
                      {t("medigap.part_b.deductible.tooltip")}
                    </p>
                  }
                >
                  {t("medigap.part_b.deductible")}
                </TooltipWithClose>
              </div>
            </div>
          </section>
        </div>
        <div className="MedigapPlanCard__divider"></div>
        <section className="MedigapPlanCard__section_column--right">
          <h3 className="MedigapPlanCard__section_heading">
            {t("medigap.card.section_heading.benefits")}
          </h3>
          <ul className="ds-c-list--bare">
            {Object.keys(planBenefitMapping).map(benefit => {
              const benefitIncluded = planBenefits.includes(benefit);
              return (
                <li
                  key={`${plan.plan_type}_benefit_${benefit}`}
                  className="MedigapPlanCard__benefit"
                >
                  <CheckmarkIcon checked={benefitIncluded} />{" "}
                  {t(planBenefitMapping[benefit])}
                  <span className="ds-u-visibility--screen-reader">
                    {benefitIncluded
                      ? ` ${t("plan_card.benefit.is_available")}`
                      : ` ${t("plan_card.benefit.is_not_available")}`}
                  </span>
                </li>
              );
            })}
          </ul>
          <div className="ds-u-margin-y--2 mct-hide-print">
            <MedigapBenefitsHelpDrawer
              triggerLabel={t("medigap.compare_to_other_plans")}
              onOpenTealiumProps={{
                event_action: "Step 1: Decide what plans you want",
                event_label: `Result: Compare to other plans ${plan.plan_type}`,
              }}
            />
          </div>
        </section>
      </div>
    </Card>
  );
};

export default MedigapPlanCard;
